import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { FiArrowLeft, FiCalendar } from 'react-icons/fi';
import Header from '../components/Header';
import Footer from '../components/layout/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { blogService, buildImageUrl } from '../services/api';
import { Container, Section, Spinner } from '../components/ui';
import Seo, { organizationSchema } from '../components/Seo';
import { useSettings } from '../contexts/SettingsContext';
import { isMissingHttpError } from '../utils/http';

interface BlogPost {
  slug: string;
  title: string;
  content: string;
  excerpt?: string;
  featured_image?: string;
  featured_image_alt?: string;
  published_at: string;
  meta_description?: string;
  modified_at?: string;
  author_name?: string;
  author_bio?: string;
  author_url?: string;
}

const Page = styled.div`min-height: 100vh; background: ${({ theme }) => theme.colors.background};`;
const Article = styled.article`max-width: 820px; margin: 0 auto;`;
const Back = styled(Link)`display: inline-flex; align-items: center; gap: .45rem; color: ${({ theme }) => theme.colors.primary}; font-weight: 600; margin-bottom: 1.4rem;`;
const Title = styled.h1`font-family: ${({ theme }) => theme.fonts.heading}; font-size: clamp(2rem, 5vw, 3.3rem); line-height: 1.12; letter-spacing: -.025em; margin: .7rem 0 1rem;`;
const DateLine = styled.div`display: flex; align-items: center; gap: .45rem; color: ${({ theme }) => theme.colors.text.secondary};`;
const Byline = styled.aside`
  margin: 1.25rem 0;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.secondary};
  a { color: ${({ theme }) => theme.colors.primary}; text-decoration: underline; }
  p + p { margin-top: .3rem; }
`;
const Cover = styled.img`width: 100%; max-height: 520px; object-fit: cover; border-radius: ${({ theme }) => theme.borderRadius.xl}; margin: 2rem 0;`;
const Content = styled.div`
  color: ${({ theme }) => theme.colors.neutral[800]};
  font-size: 1.05rem;
  line-height: 1.85;
  p, ul, ol, blockquote, h2, h3, h4 { margin: 1.2rem 0; }
  h2, h3, h4 { color: ${({ theme }) => theme.colors.foreground}; line-height: 1.3; }
  img { max-width: 100%; height: auto; border-radius: ${({ theme }) => theme.borderRadius.lg}; }
  a { color: ${({ theme }) => theme.colors.primary}; text-decoration: underline; }
  blockquote { border-left: 4px solid ${({ theme }) => theme.colors.primary}; padding-left: 1rem; color: ${({ theme }) => theme.colors.text.secondary}; }
`;
const State = styled.div`min-height: 360px; display: grid; place-items: center; color: ${({ theme }) => theme.colors.text.secondary};`;

const authorLink = (value?: string): string | undefined => {
  if (!value || !(/^https?:\/\//i.test(value) || /^\/(?!\/)/.test(value))) return undefined;
  try {
    const url = new URL(value, window.location.origin);
    return ['https:', 'http:'].includes(url.protocol) ? url.href : undefined;
  } catch {
    return undefined;
  }
};

const BlogPostPage: React.FC = () => {
  const { siteSettings } = useSettings();
  const { slug = '' } = useParams();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    setLoading(true);
    setMissing(false);
    setLoadFailed(false);
    setPost(null);
    blogService.getPost(slug)
      .then((value) => {
        setPost(value);
      })
      .catch((error) => {
        if (isMissingHttpError(error)) setMissing(true);
        else setLoadFailed(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const date = post && new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' }).format(new Date(post.published_at));
  const authorUrl = authorLink(post?.author_url);
  return (
    <Page>
      {post && (
        <Seo
          title={`${post.title} | ${siteSettings?.company_info?.name || siteSettings?.site_name || 'Imobiliária'}`}
          description={(post.meta_description || post.excerpt || post.content.replace(/<[^>]+>/g, ' ')).replace(/\s+/g, ' ').slice(0, 158)}
          canonicalPath={`/blog/${post.slug}`}
          image={post.featured_image ? buildImageUrl(post.featured_image) : undefined}
          type="article"
          structuredData={[
            organizationSchema(siteSettings),
            {
              '@type': 'BlogPosting',
              headline: post.title,
              datePublished: post.published_at,
              dateModified: post.modified_at || post.published_at,
              mainEntityOfPage: `${window.location.origin}/blog/${post.slug}`,
              publisher: { '@id': `${window.location.origin}/#organization` },
              ...(post.author_name ? { author: {
                '@type': 'Person', name: post.author_name, ...(authorUrl ? { url: authorUrl } : {}),
              } } : {}),
            },
          ]}
        />
      )}
      {missing && (
        <Seo
          title="Artigo não encontrado"
          description="O artigo solicitado não foi encontrado."
          canonicalPath={`/blog/${slug}`}
          robots="noindex,nofollow"
        />
      )}
      <Header />
      <WhatsAppButton />
      <Section>
        <Container>
          {loading ? <State><Spinner $size={34} /></State> : missing ? (
            <State><div>Artigo não encontrado. <Link to="/blog">Voltar ao blog</Link></div></State>
          ) : loadFailed || !post ? (
            <State><div>Não foi possível carregar o artigo agora. <Link to="/blog">Voltar ao blog</Link></div></State>
          ) : (
            <Article>
              <Back to="/blog"><FiArrowLeft /> Voltar ao blog</Back>
              <DateLine><FiCalendar /> {date}</DateLine>
              <Title>{post.title}</Title>
              {post.author_name && <Byline aria-label="Sobre quem escreveu">
                <p>Por {authorUrl ? <a href={authorUrl} rel="author">{post.author_name}</a> : post.author_name}</p>
                {post.author_bio && <p>{post.author_bio}</p>}
              </Byline>}
              {post.featured_image && <Cover src={buildImageUrl(post.featured_image)} alt={post.featured_image_alt || post.title} />}
              <Content dangerouslySetInnerHTML={{ __html: post.content }} />
            </Article>
          )}
        </Container>
      </Section>
      <Footer />
    </Page>
  );
};

export default BlogPostPage;
